/**
 * TopBar — slim 56px sticky top navigation bar.
 * Left: logo + breadcrumbs. Center: search pill (opens CommandPalette). Right: notifications + help + avatar.
 * Sits at left: sidebarWidth so it doesn't overlap the sidebar rail.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  HelpOutline as HelpIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  NavigateNext as NavNextIcon,
} from '@mui/icons-material';
import NotificationBell from '../NotificationBell';
import { tokens } from '../../theme';
import { AuthContext } from '../AuthContext';

const TOP_BAR_HEIGHT = 56;

// Maps path segments to human-readable labels
const SEGMENT_LABELS = {
  dashboard: 'Dashboard',
  client_list: 'Clients',
  billing: 'Billing',
  invoices: 'Invoices',
  payments: 'Payments',
  statements: 'Statements',
  expenses: 'Expenses',
  credits: 'Credits',
  files: 'Files',
  tasks: 'Tasks',
  workflows: 'Workflows',
  templates: 'Templates',
  settings: 'Settings',
  inbox: 'Inbox',
  'tax-organizers': 'Tax Organizers',
  reports: 'Reports',
  'aged-receivables': 'Aged Receivables',
  profile: 'My Profile',
  notifications: 'Notifications',
  company: 'Company Profile',
  'billing-settings': 'Billing & Seats',
  'custom-branding': 'Custom Branding',
  'email-templates': 'Email Templates',
  integrations: 'Integrations',
  developer: 'Developer',
  tags: 'Tags',
  'team-organization': 'Team Members',
  'email-calendar': 'Email & Calendar',
  security: 'Security',
  'access-permissions': 'Access & Permissions',
};

function buildBreadcrumbs(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0 || (segments.length === 1 && segments[0] === 'dashboard')) {
    return [{ label: 'Dashboard', path: '/dashboard' }];
  }
  const crumbs = [];
  let accumulated = '';
  for (const seg of segments) {
    accumulated += `/${seg}`;
    const label = SEGMENT_LABELS[seg];
    // Skip numeric IDs in breadcrumbs — they'll be resolved to names by parent pages if needed
    if (!label && /^\d+$/.test(seg)) continue;
    crumbs.push({ label: label || seg, path: accumulated });
  }
  return crumbs;
}

function openCommandPalette() {
  window.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true })
  );
}

export { TOP_BAR_HEIGHT };

const TopBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = React.useContext(AuthContext) || {};
  const [avatarAnchor, setAvatarAnchor] = useState(null);
  const [userInitials, setUserInitials] = useState('');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        const name = u.username || u.first_name || '';
        setUserName(name);
        setUserInitials(name.slice(0, 2).toUpperCase());
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  const handleSignOut = useCallback(() => {
    setAvatarAnchor(null);
    if (logout) {
      logout();
    } else {
      ['access_token', 'refresh_token', 'user', 'userId', 'jwtToken', 'refreshToken'].forEach(
        (k) => localStorage.removeItem(k)
      );
      navigate('/login');
    }
  }, [logout, navigate]);

  const crumbs = buildBreadcrumbs(location.pathname);

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: tokens.sidebarWidth,
        right: 0,
        height: TOP_BAR_HEIGHT,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        gap: 1.5,
        backdropFilter: 'blur(8px)',
        bgcolor: (t) => alpha(t.palette.background.paper, 0.82),
        borderBottom: 1,
        borderColor: 'divider',
        boxSizing: 'border-box',
      }}
    >
      {/* Left: logo + breadcrumbs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexShrink: 1, mr: 1 }}>
        <RouterLink
          to="/dashboard"
          style={{ textDecoration: 'none', flexShrink: 0 }}
          aria-label="Go to Dashboard"
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: '-0.03em',
              color: 'primary.main',
              lineHeight: 1,
              userSelect: 'none',
            }}
          >
            Firmflow
          </Typography>
        </RouterLink>

        {crumbs.length > 0 && (
          <Breadcrumbs
            separator={<NavNextIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              '& .MuiBreadcrumbs-li': { whiteSpace: 'nowrap' },
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {crumbs.map((crumb, idx) => {
              const isLast = idx === crumbs.length - 1;
              return isLast ? (
                <Typography
                  key={crumb.path}
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', fontSize: 13 }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path}
                  component={RouterLink}
                  to={crumb.path}
                  underline="hover"
                  sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}
                >
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        )}
      </Box>

      {/* Center: search pill */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 1 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={openCommandPalette}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openCommandPalette(); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            maxWidth: 480,
            width: '100%',
            height: 34,
            px: 1.75,
            borderRadius: 999,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            cursor: 'pointer',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': {
              borderColor: 'primary.light',
              boxShadow: (t) => `0 0 0 3px ${t.palette.primary.light}40`,
            },
            '&:focus-visible': {
              outline: 'none',
              borderColor: 'primary.main',
              boxShadow: (t) => `0 0 0 3px ${t.palette.primary.light}60`,
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              fontSize: 13,
              flex: 1,
              userSelect: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Search clients, tasks, invoices…
          </Typography>
          <Typography
            variant="caption"
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.disabled',
              fontSize: 11,
              fontFamily: 'monospace',
              flexShrink: 0,
              lineHeight: 1.6,
            }}
          >
            ⌘K
          </Typography>
        </Box>
      </Box>

      {/* Right: actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
        <NotificationBell />

        <Tooltip title="Help" placement="bottom">
          <IconButton
            size="small"
            href="https://docs.firmflow.app"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: 'text.secondary' }}
            aria-label="Help"
          >
            <HelpIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Account" placement="bottom">
          <IconButton
            size="small"
            onClick={(e) => setAvatarAnchor(e.currentTarget)}
            sx={{ p: 0.25 }}
            aria-label="Open account menu"
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              {userInitials || <PersonIcon sx={{ fontSize: 16 }} />}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Avatar dropdown menu */}
      <Menu
        anchorEl={avatarAnchor}
        open={Boolean(avatarAnchor)}
        onClose={() => setAvatarAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 180, borderRadius: 2 } } }}
      >
        {userName && (
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{userName}</Typography>
          </Box>
        )}
        {userName && <Divider />}
        <MenuItem
          onClick={() => { setAvatarAnchor(null); navigate('/settings/profile'); }}
          sx={{ gap: 1.5, fontSize: 14 }}
        >
          <SettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          My Profile
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut} sx={{ gap: 1.5, fontSize: 14, color: 'error.main' }}>
          <LogoutIcon sx={{ fontSize: 18 }} />
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TopBar;
